import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

type MonthlyData = {
  month: number;
  year: number;
  revenue: number;
  activeMerchants: number;
};

type RevenueGraphProps = {
  data: MonthlyData[] | undefined;
  isLoading: boolean;
  trmValue: number;
  title: string;
};

const RevenueGraph = ({ data, isLoading, trmValue, title }: RevenueGraphProps) => {
  if (isLoading) {
    return (
      <Card className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6">
        <div className="flex items-center justify-center py-20">
          <Spinner />
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6">
        <p className="text-md font-semibold text-gray-700 mb-4">{title}</p>
        <div className="flex items-center justify-center py-20">
          <p className="text-gray-500">No data available</p>
        </div>
      </Card>
    );
  }

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  const chartData = data.map((item) => ({
    name: `${monthNames[item.month - 1]} ${item.year}`,
    MRR: Math.round(item.revenue / trmValue),
    activeMerchants: item.activeMerchants,
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Card className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6">
      <p className="text-md font-semibold text-gray-700 mb-4">{title}</p>
      
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis
            label={{ value: "MRR (USD)", angle: -90, position: "insideLeft" }}
            tickFormatter={formatCurrency}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length > 0) {
                const chartData = payload[0].payload;
                return (
                  <div className="bg-white p-3 border border-gray-200 border-radius-md shadow-md">
                    <p className="text-sm font-semibold mb-2">{chartData.name}</p>
                    <p className="text-sm text-primary">MRR: {formatCurrency(chartData.MRR)}</p>
                    <p className="text-sm text-gray-600">Active Merchants: {chartData.activeMerchants}</p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="MRR"
            stroke="#4012AB"
            strokeWidth={2}
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default RevenueGraph;
